import React from 'react';
import { Card } from 'antd';
import { analyticsData } from '../mockData';
import s from './s.module.less';

const statusColors: Record<string, string> = {
    submitted: '#faad14',
    under_review: '#1890ff',
    approved: '#52c41a',
    rejected: '#ff4d4f',
    more_info_needed: '#fa8c16',
};

const statusLabels: Record<string, string> = {
    submitted: 'Submitted',
    under_review: 'Under Review',
    approved: 'Approved',
    rejected: 'Rejected',
    more_info_needed: 'More Info Needed',
};

const profileStatusColors: Record<string, string> = {
    listed: '#52c41a',
    pause: '#faad14',
    pending_review: '#1890ff',
    unlisted: '#ff4d4f',
    disqualified: '#fa8c16',
};

const profileStatusLabels: Record<string, string> = {
    listed: 'Listed',
    pause: 'Pause',
    pending_review: 'Pending Review',
    unlisted: 'Unlisted',
    disqualified: 'Disqualified',
};

interface BarChartProps {
    data: Record<string, number>;
    colorMap?: Record<string, string>;
    labelMap?: Record<string, string>;
    defaultColor?: string;
    preserveOrder?: boolean;
}

const BarChart: React.FC<BarChartProps> = ({ data, colorMap, labelMap, defaultColor = '#13c2c2', preserveOrder }) => {
    const maxVal = Math.max(...Object.values(data), 1);
    const entries = Object.entries(data);
    const sorted = preserveOrder ? entries : entries.sort((a, b) => b[1] - a[1]);

    return (
        <div>
            {sorted.map(([key, value]) => (
                <div className={s.barRow} key={key}>
                    <span className={s.barLabel}>{labelMap?.[key] || key}</span>
                    <div className={s.barTrack}>
                        <div
                            className={s.barFill}
                            style={{
                                width: `${(value / maxVal) * 100}%`,
                                backgroundColor: colorMap?.[key] || defaultColor,
                            }}
                        />
                    </div>
                    <span className={s.barCount}>{value}</span>
                </div>
            ))}
        </div>
    );
};

const GPAnalyticsPage: React.FC = () => {
    const { totalApplications, avgReviewDays, approvalRate, activePractices, statusCounts, channelCounts, specialtyCounts, profileStatusCounts } = analyticsData;

    return (
        <div className={s.wrap}>
            <div className={s.header}>
                <h2>Data Overview</h2>
            </div>

            <div className={s.kpiRow}>
                <Card className={s.kpiCard}>
                    <div className={s.kpiValue}>{totalApplications}</div>
                    <div className={s.kpiLabel}>Total Applications</div>
                </Card>
                <Card className={s.kpiCard}>
                    <div className={s.kpiValue}>{avgReviewDays} days</div>
                    <div className={s.kpiLabel}>Avg Review Time</div>
                </Card>
                <Card className={s.kpiCard}>
                    <div className={s.kpiValue}>{approvalRate}%</div>
                    <div className={s.kpiLabel}>Approval Rate</div>
                </Card>
                <Card className={s.kpiCard}>
                    <div className={s.kpiValue}>{activePractices}</div>
                    <div className={s.kpiLabel}>Active Practices</div>
                </Card>
            </div>

            <div className={s.chartsGrid}>
                <Card title="Application Funnel" className={s.chartCard}>
                    <BarChart
                        data={statusCounts}
                        colorMap={statusColors}
                        labelMap={statusLabels}
                        preserveOrder
                    />
                </Card>
                <Card title="Providers by Channel" className={s.chartCard}>
                    <BarChart
                        data={channelCounts}
                        defaultColor="#13c2c2"
                    />
                </Card>
                <Card title="Practices by Specialty" className={s.chartCard}>
                    <BarChart
                        data={specialtyCounts}
                        defaultColor="#722ed1"
                    />
                </Card>
                <Card title="Provider Profile Status" className={s.chartCard}>
                    <BarChart
                        data={profileStatusCounts}
                        colorMap={profileStatusColors}
                        labelMap={profileStatusLabels}
                        preserveOrder
                    />
                </Card>
            </div>
        </div>
    );
};

export default GPAnalyticsPage;
